import { BaseUserProfile } from 'fitbeat.models';
import React, { Component } from 'react';
import { translate } from 'react-i18next';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { connect } from 'react-redux';
import { TranslateProp } from '../common-types';
import theme from '../theme';
import { Logger, UserDataConsumer } from '../utils';
import { defaultScale } from '../utils/scaling';
import {
  BluetoothServiceSingleton, DisplaySettingsModal, ProfileHeartRateSetup,
  RetryPolicy, StartHeartRateTestScan,
} from '../HomeWorkouts';
import { enabledFeaturesSelector, hasAccessToAStudioGymSelector } from '../auth/auth-selectors';
import {
  toggleHeartRateMonitorModalStudioVisible, toggleHeartRateMonitorModalVisible,
  toggleScreenMirroringModalVisible,
} from '../ui/redux/uiActions';
import { userAnsweredHasScaleQuestion } from '../WeightTarget/actions';
import ConnectedAppsManagerSingleton from '../ConnectedApps';
import ConnectedWorkoutApp from '../ConnectedApps/connected-workout-app';
import Healthkit from '../ConnectedApps/HealthKit/healthkit';
import ConnectedAppTile from './components/ConnectedAppTile';
import AppleWatchConnectedDevice from './components/AppleWatchConnectedDevice';
import ConnectedItem from './components/ConnectedItem';

interface IProps {
  t: TranslateProp;
  setUpSmartScale: () => void;
  user: any; /* UserDataConsumer */
  hasAccessToAStudioGym: boolean;
  toggleHRMonitorModal: () => void;
  toggleHeartRateMonitorModalForStudio: () => void;
  toggleScreenMirroringModal: () => void;
}

interface IState {
  isHealthEnabled: boolean;
}

class ConnectedApps extends Component<IProps, IState> {
  constructor(props) {
    super(props);
    this.state = {
      isHealthEnabled: false,
    };
  }

  public componentDidMount() {
    this.determineIsHealthEnabled();
  }

  bluetoothService = BluetoothServiceSingleton.getBluetoothService();

  openHRMonitorModalForHome = () => {
    const { toggleHRMonitorModal } = this.props;
    this.bluetoothService.setRetryPolicy(
      RetryPolicy.StopAfterRetryCountExceeded,
    );
    toggleHRMonitorModal();
  };

  openHRMonitorModalForStudio = () => {
    this.props.toggleHeartRateMonitorModalForStudio();
  };

  public openScreenMirroringModel = () => {
    const { toggleScreenMirroringModal } = this.props;
    toggleScreenMirroringModal();
  };

  public getConnectedAppsForCurrentUser(): ConnectedWorkoutApp[] {
    const apps = ConnectedAppsManagerSingleton.connectedApps;
    return apps;
  }

  determineIsHealthEnabled = () => {
    Healthkit.allRequestedPermissionsGranted()
      .then((allPermissionsGranted) => {
        this.setState(() => ({
          isHealthEnabled: allPermissionsGranted,
        }));
      })
      .catch((err) => Logger.notify(err));
  };

  public mapConnectedApps(connectedAppsList: ConnectedWorkoutApp[]) {
    const { t, user: { userProfile } } = this.props;
    if (!connectedAppsList.length) {
      return (
        <View style={styles.itemContainer}>
          <View style={styles.iconLabelContainer}>
            <Text style={[theme.textStyle.bodyCopyReg, styles.itemLabel]}>
              {t('noConnectedApps')}
            </Text>
          </View>
        </View>
      );
    }
    return connectedAppsList.map((singleApp: ConnectedWorkoutApp, index, { length }) => {
      const isLastItem = length - 1 === index;

      return (
        <ConnectedAppTile
          key={singleApp.name}
          appDetails={singleApp}
          isLastItem={isLastItem}
          appCallback={this.determineIsHealthEnabled}
        />
      );
    });
  }

  public render() {
    const {
      t,
      setUpSmartScale,
      user: { userProfile, userProfileService },
      hasAccessToAStudioGym,
    } = this.props;

    const userHasHomeHRM = userProfile.heartRateMonitors?.home;
    const userHasStudioHRM = userProfile.heartRateMonitors?.studio;
    const connectedApps = this.mapConnectedApps(this.getConnectedAppsForCurrentUser());
    return (
      <ScrollView style={styles.mainContainer}>
        <View style={styles.connectedItemsContainer}>
          <Text
            style={[theme.textStyle.heading2, styles.connectedItemsHeading]}
          >
            {t('connectedApps')}
          </Text>
          <Text
            style={[
              theme.textStyle.bodyCopyReg,
              styles.connectedItemsSubHeading,
            ]}
          >
            {t('Sync your workouts')}
          </Text>
          {connectedApps}
        </View>
        <View style={styles.connectedItemsContainer}>
          <Text
            style={[theme.textStyle.heading2, styles.connectedItemsHeading]}
          >
            {t('connectedDevices')}
          </Text>
          <Text
            style={[
              theme.textStyle.bodyCopyReg,
              styles.connectedItemsSubHeading,
            ]}
          >
            {t('Track your stats and progress')}
          </Text>
          <AppleWatchConnectedDevice t={t} isEnabled={this.state.isHealthEnabled} />

          <ConnectedItem
            t={t}
            onPress={this.openHRMonitorModalForHome}
            title={t('settings:homeHeartRateMonitor')}
            subTitle={t('settings:homeHeartRateMonitorSubTitle')}
            buttonTitle={userHasHomeHRM ? t('manage') : t('connect')}
          />
          {hasAccessToAStudioGym && (
            <ConnectedItem
              t={t}
              onPress={this.openHRMonitorModalForStudio}
              title={t('settings:studioHeartRateMonitor')}
              subTitle={t('settings:studioHeartRateMonitorSubTitle')}
              buttonTitle={userHasStudioHRM ? t('manage') : t('connect')}
            />
          )}
          <ConnectedItem
            t={t}
            onPress={setUpSmartScale}
            title={t('smartScale')}
            buttonTitle={t('setup')}
          />
          <ConnectedItem
            t={t}
            onPress={this.openScreenMirroringModel}
            title={t('screenCastingDevice')}
            buttonTitle={t('setup')}
          />
        </View>
        <DisplaySettingsModal />
        {Platform.OS !== 'web' && (
          <ProfileHeartRateSetup
            bluetoothService={this.bluetoothService}
            userProfile={userProfile}
            heartRateScanAction={StartHeartRateTestScan}
            userUpdateFunc={userProfileService.updateUserProfileAsync}
            updateUserHeartRateMonitors={userProfileService.updateUserHeartRateMonitors}
          />
        )}
      </ScrollView>
    );
  }
}
const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: theme.GREY_LIGHT,
    paddingVertical: defaultScale(10),
  },
  connectedItemsContainer: {
    flexDirection: 'column',
    justifyContent: 'center',
    padding: defaultScale(20),
    paddingBottom: defaultScale(0),
    backgroundColor: theme.WHITE,
    marginBottom: defaultScale(10),
  },
  connectedItemsHeading: {
    marginBottom: defaultScale(8),
  },
  connectedItemsSubHeading: {
    marginBottom: defaultScale(20),
  },
  noTopBorder: {
    borderTopWidth: 0,
  },
  itemContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: defaultScale(20),
    borderTopWidth: 1,
    borderColor: theme.GREY_LIGHT,
  },
  itemLabel: {
    fontWeight: '400',
    color: theme.CHARCOAL,
  },
  iconLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

const mapStateToProps = (state) => {
  const { heartRateMonitorModalVisible, screenMirroringModalVisible } = state.ui;
  return {
    heartRateMonitorModalVisible,
    screenMirroringModalVisible,
    enabledFeatures: enabledFeaturesSelector(state),
    hasAccessToAStudioGym: hasAccessToAStudioGymSelector(state),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { userProfile } = ownProps.user;
  return {
    toggleHRMonitorModal: () => dispatch(toggleHeartRateMonitorModalVisible(true)),
    toggleHeartRateMonitorModalForStudio: () => dispatch(toggleHeartRateMonitorModalStudioVisible(true)),
    toggleScreenMirroringModal: () => dispatch(toggleScreenMirroringModalVisible(true)),
    setUpSmartScale: () => dispatch(userAnsweredHasScaleQuestion(true, userProfile)),
  };
};

export default UserDataConsumer(connect(mapStateToProps, mapDispatchToProps)(
  translate(['settings'], { wait: true })(ConnectedApps),
));
